import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { StyledButton } from "../../atoms/button";
import {
  changeLocale,
  fetchLocales,
  getCurrentLocale,
} from "../../../i18n";
import "./style.scss";

const LanguageSwitcher = ({ className = "", variant = "default" }) => {
  const { t, i18n } = useTranslation();
  const [locales, setLocales] = useState([]);
  const [current, setCurrent] = useState(getCurrentLocale());
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchLocales().then((codes) => {
      if (!cancelled && codes?.length) {
        setLocales([...codes]);
        setCurrent(getCurrentLocale());
      }
    });
    const onLang = () => setCurrent(getCurrentLocale());
    i18n.on("languageChanged", onLang);
    return () => {
      cancelled = true;
      i18n.off("languageChanged", onLang);
    };
  }, [i18n]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (variant === "nav") {
    return (
      <div
        ref={rootRef}
        className={`language-switcher language-switcher--nav ${className}`.trim()}
      >
        <StyledButton
          size="lg"
          bg="darkgray"
          type="icon"
          tag="a"
          aria-label={t("locale.label")}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((prev) => !prev)}
        >
          <SVG src="assets/images/common/icon_language.svg" />
        </StyledButton>
        {open && (
          <ul className="language-switcher__menu" role="listbox" aria-label={t("locale.label")}>
            {locales.map((lng) => (
              <li key={lng} role="option" aria-selected={current === lng}>
                <button
                  type="button"
                  className={`language-switcher__option${current === lng ? " is-active" : ""}`}
                  onClick={async () => {
                    await changeLocale(lng);
                    setCurrent(lng);
                    setOpen(false);
                  }}
                >
                  {t(`locale.${lng}`, { defaultValue: lng.toUpperCase() })}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <label className={`language-switcher ${className}`.trim()}>
      <span className="language-switcher__label">{t("locale.label")}</span>
      <select
        value={current}
        aria-label={t("locale.label")}
        onChange={async (e) => {
          const next = e.target.value;
          await changeLocale(next);
          setCurrent(next);
        }}
      >
        {locales.map((lng) => (
          <option key={lng} value={lng}>
            {t(`locale.${lng}`, { defaultValue: lng.toUpperCase() })}
          </option>
        ))}
      </select>
    </label>
  );
};

export default LanguageSwitcher;
